import { Close, Search } from '@mui/icons-material';
import { IconButton, InputAdornment, Stack, TextField } from '@mui/material';
import { useProductsTFunction } from '@retail/products/i18n';
import { KeyboardEventHandler, ChangeEvent, useCallback, useEffect, useId, useState } from 'react';
import TextButton from '../Buttons/TextButton';
import { FilterField } from './FilterField';

export interface SearchFilterFieldProps {
  placeholder: string;
  label?: string;
  searchTerm?: string;
  isDisabled?: boolean;
  size?: 'small' | 'medium';
  width?: string | number;
  onSubmitSearch: (search: string) => void;
}

export function SearchFilterField({
  placeholder,
  label,
  searchTerm,
  isDisabled = false,
  size = 'medium',
  width,
  onSubmitSearch,
}: SearchFilterFieldProps) {
  const t = useProductsTFunction();
  const inputId = useId();

  const [searchFieldInput, setSearchFieldInput] = useState(searchTerm ?? '');

  const submitSearch = useCallback(
    () => onSubmitSearch(searchFieldInput),
    [onSubmitSearch, searchFieldInput]
  );

  const onKeyDown: KeyboardEventHandler = useCallback(
    (e) => {
      if (e.key === 'Enter') submitSearch();
    },
    [submitSearch]
  );

  const reset = useCallback(() => {
    setSearchFieldInput('');
    onSubmitSearch('');
  }, [onSubmitSearch]);

  useEffect(() => {
    setSearchFieldInput(searchTerm ?? '');
  }, [searchTerm]);

  return (
    <FilterField label={label} htmlFor={inputId}>
      <TextField
        id={inputId}
        sx={{ width }}
        size={size}
        variant="outlined"
        disabled={isDisabled}
        placeholder={placeholder}
        value={searchFieldInput}
        onKeyDown={onKeyDown}
        autoComplete="off"
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" color="disabled" />
              </InputAdornment>
            ),
            endAdornment: searchFieldInput ? (
              <Stack flexDirection="row">
                <TextButton onClick={submitSearch}>
                  {t('products.search.openSearch.search')}
                </TextButton>
                <IconButton size="small" onClick={reset} edge="end">
                  <Close fontSize="small" />
                </IconButton>
              </Stack>
            ) : undefined,
          },
        }}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          setSearchFieldInput(event.target.value);
          if (event.target.value === '') onSubmitSearch('');
        }}
      />
    </FilterField>
  );
}
